import React, { useState, useEffect, useMemo } from "react";
import { Tabs, Typography, Skeleton, Tooltip } from "antd";
import {
  MessageOutlined,
  CustomerServiceOutlined,
  CopyOutlined,
  AppstoreOutlined,
  StarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import AttributeList from "./AttributeList";
import "./AttributeTabs.css";
import { useSelector } from "react-redux";

const { TabPane } = Tabs;

const AttributeTabs = ({
  scrollToSelected = true,
  height = 800,
  firstPage,
  setShowChartSide,
}) => {
  // State - Initialize with first available tab
  const [activeTab, setActiveTab] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabSearchResults, setTabSearchResults] = useState({});

  // Redux selectors
  const authParams = useSelector((state) => state.Authentication.authParams);
  const isshowSideBar = useSelector((state) => state.WidgetMaker.isshowSideBar);
  const chartAttributesFieldConfig = useSelector(
    (state) => state?.WidgetMaker?.chart_attributes_field_config
  );
  const WM_attributes = useSelector((state) => state.WidgetMaker.WM_attributes);
  const graphInfoLoader = useSelector(
    (state) => state?.WidgetMaker?.graph_info_loader
  );

  // Theme
  const isDarkTheme = authParams?.theme_type === "1";
  const themeClass = isDarkTheme ? "darkTheme" : "";

  // Calculate attribute counts for each tab and determine available tabs
  const { getAttributeCounts, availableTabs } = useMemo(() => {
    // Default values - ALWAYS include favorites tab
    const defaultResult = {
      getAttributeCounts: {
        common: 0,
        author: 0,
        mentions: 0,
        tickets: 0,
        pages: 0,
        favorites: 0,
        total: 0,
      },
      availableTabs: ["author", "mentions", "tickets", "pages", "favorites"], // Always include favorites
    };

    if (!chartAttributesFieldConfig?.data) {
      return defaultResult;
    }

    // Handle special field naming cases
    let selectedField = chartAttributesFieldConfig?.selected_field;
    if (selectedField?.startsWith("y_series_")) {
      selectedField = "y_series";
    } else if (selectedField?.startsWith("y_axes_")) {
      selectedField = "y_axes";
    } else if (selectedField?.startsWith("filter_dropdown_")) {
      selectedField = "filter_dropdown";
    }

    const fieldConfig = chartAttributesFieldConfig.data.find(
      (field) => field.payload_key_name === selectedField
    );

    if (!fieldConfig?.attributes) {
      return defaultResult;
    }

    let commonCount = 0;
    let authorCount = 0;
    let mentionsCount = 0;
    let ticketsCount = 0;
    let pagesCount = 0;
    let favouritesCount = 0;
    const tabs = [];

    // Count attributes by data source
    const attributes = fieldConfig.attributes;

    // Debug log to see what we have
    // console.log("Field attributes:", attributes);

    // Check common attributes - only show if has actual members
    if (attributes.common) {
      const commonKeys = Object.keys(attributes.common);
      commonKeys.forEach((key) => {
        const group = attributes.common[key];
        if (group && group.members && Object.keys(group.members).length > 0) {
          commonCount += Object.keys(group.members).length;
        }
      });
      if (commonCount > 0) {
        tabs.push("common");
      }
    }

    // Check author attributes
    if (attributes.author) {
      const authorKeys = Object.keys(attributes.author);
      authorKeys.forEach((key) => {
        const group = attributes.author[key];
        if (group && group.members && Object.keys(group.members).length > 0) {
          authorCount += Object.keys(group.members).length;
        }
      });
      if (authorCount > 0) {
        tabs.push("author");
      }
    }

    // Always check and show mentions if it exists
    if (attributes.mentions) {
      const mentionKeys = Object.keys(attributes.mentions);
      mentionKeys.forEach((key) => {
        const group = attributes.mentions[key];
        if (group && group.members && Object.keys(group.members).length > 0) {
          mentionsCount += Object.keys(group.members).length;
        }
      });
      tabs.push("mentions"); // Always show mentions tab if the key exists
    }

    // Always check and show tickets if it exists
    if (attributes.tickets) {
      const ticketKeys = Object.keys(attributes.tickets);
      ticketKeys.forEach((key) => {
        const group = attributes.tickets[key];
        if (group && group.members && Object.keys(group.members).length > 0) {
          ticketsCount += Object.keys(group.members).length;
        }
      });
      tabs.push("tickets"); // Always show tickets tab if the key exists
    }

    // Check for both 'page' and 'pages' keys for compatibility
    const pageAttributes = attributes.page || attributes.pages;
    if (pageAttributes) {
      const pageKeys = Object.keys(pageAttributes);
      pageKeys.forEach((key) => {
        const group = pageAttributes[key];
        if (group && group.members && Object.keys(group.members).length > 0) {
          pagesCount += Object.keys(group.members).length;
        }
      });
      tabs.push("pages"); // Always show pages tab if the key exists
    }

    // Always show Favourites tab, regardless of content
    const favouritesAttributes = attributes.favourites || attributes.favorites;
    if (favouritesAttributes) {
      // If it's an array, count the array length
      if (Array.isArray(favouritesAttributes)) {
        favouritesCount = favouritesAttributes.length;
      }
      // If it's an object with groups like other tabs
      else if (typeof favouritesAttributes === "object") {
        const favouriteKeys = Object.keys(favouritesAttributes);
        favouriteKeys.forEach((key) => {
          const group = favouritesAttributes[key];
          if (group && group.members && Object.keys(group.members).length > 0) {
            favouritesCount += Object.keys(group.members).length;
          }
        });
      }
    }
    // ALWAYS add favourites tab for all fields
    tabs.push("favourites");

    // If no other tabs were found, show default tabs
    if (tabs.length === 1) {
      // Only favourites exists
      tabs.unshift("author", "mentions", "tickets", "pages"); // Add default tabs before favourites
    }

    return {
      getAttributeCounts: {
        common: commonCount,
        author: authorCount,
        mentions: mentionsCount,
        tickets: ticketsCount,
        pages: pagesCount,
        favourites: favouritesCount,
        total:
          commonCount +
          authorCount +
          mentionsCount +
          ticketsCount +
          pagesCount +
          favouritesCount,
      },
      availableTabs: tabs,
    };
  }, [chartAttributesFieldConfig]);

  // Set initial active tab when available tabs change
  useEffect(() => {
    if (availableTabs.length > 0) {
      // If current active tab is not available, switch to first available tab
      if (!activeTab || !availableTabs.includes(activeTab)) {
        setActiveTab(availableTabs[0]);
      }
    }
  }, [availableTabs]);

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  // Function to check if an attribute exists in a tab's data
  const checkAttributeInTab = (tabData, search) => {
    if (!tabData || !search) return false;
    const searchLower = search.toLowerCase();

    // For each group in the tab data
    const tabGroups = Object.values(tabData || {});
    return tabGroups.some(group => {
      if (!group || !group.members) return false;

      // Check each attribute in the group
      return Object.values(group.members || {}).some(attr => {
        return (
          attr.title?.toLowerCase().includes(searchLower) ||
          attr.attribute?.toLowerCase().includes(searchLower) ||
          attr.description?.toLowerCase().includes(searchLower)
        );
      });
    });
  };

  // Handle search from child component and auto-switch tabs
  const handleAttributeSearch = (searchValue) => {
    setSearchTerm(searchValue);

    if (!searchValue || !chartAttributesFieldConfig?.data) return;

    // Get current field config
    let selectedField = chartAttributesFieldConfig?.selected_field;
    if (selectedField?.startsWith("y_series_")) {
      selectedField = "y_series";
    } else if (selectedField?.startsWith("y_axes_")) {
      selectedField = "y_axes";
    } else if (selectedField?.startsWith("filter_dropdown_")) {
      selectedField = "filter_dropdown";
    }

    const fieldConfig = chartAttributesFieldConfig.data.find(
      (field) => field.payload_key_name === selectedField
    );

    if (!fieldConfig?.attributes) return;

    // Check each tab for matching attributes
    const results = {};
    let foundInCurrentTab = false;
    let firstMatchingTab = null;

    availableTabs.forEach(tab => {
      let tabData;

      if (tab === 'pages') {
        // Handle pages/page naming inconsistency
        tabData = fieldConfig.attributes.page || fieldConfig.attributes.pages;
      } else if (tab === 'favourites') {
        // Handle favourites/favorites naming
        tabData = fieldConfig.attributes.favourites || fieldConfig.attributes.favorites;
        // If favourites is an array, convert to searchable format
        if (Array.isArray(tabData)) {
          const tempData = { group: { members: {} } };
          tabData.forEach((item, index) => {
            tempData.group.members[index] = item;
          });
          tabData = tempData;
        }
      } else {
        tabData = fieldConfig.attributes[tab];
      }

      const hasMatch = checkAttributeInTab(tabData, searchValue);
      results[tab] = hasMatch;

      if (hasMatch) {
        if (!firstMatchingTab) firstMatchingTab = tab;
        if (tab === activeTab) foundInCurrentTab = true;
      }
    });

    setTabSearchResults(results);

    // If no results in current tab but found in another tab, switch to it
    if (!foundInCurrentTab && firstMatchingTab && searchValue) {
      setActiveTab(firstMatchingTab);
    }
  };

  return (
    <div className={`wm-attr-tabs-container ${themeClass}`}>
      {/* Header Section */}

      {isshowSideBar ? (
        ""
      ) : (
        <div className="wm-attr-tabs-header">
          {/* Tabs Section */}
          <div className="wm-attr-tabs-section">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="wm-attr-tabs"
              // style={{ marginLeft: "-15px" }}
            >
              {availableTabs.includes("common") && (
                <TabPane
                  tab={
                    <Tooltip title={`Common`} placement="bottom">
                      <span className="wm-attr-tab-item">
                        <AppstoreOutlined className="wm-attr-tab-icon" />
                      </span>
                    </Tooltip>
                  }
                  key="common"
                />
              )}
              {availableTabs.includes("author") && (
                <TabPane
                  tab={
                    <Tooltip title={`Author`} placement="bottom">
                      <span className="wm-attr-tab-item">
                        <UserOutlined className="wm-attr-tab-icon" />
                      </span>
                    </Tooltip>
                  }
                  key="author"
                />
              )}
              {availableTabs.includes("mentions") && (
                <TabPane
                  tab={
                    <Tooltip title={`Mentions`} placement="bottom">
                      <span className="wm-attr-tab-item">
                        <MessageOutlined className="wm-attr-tab-icon" />
                      </span>
                    </Tooltip>
                  }
                  key="mentions"
                />
              )}
              {availableTabs.includes("tickets") && (
                <TabPane
                  tab={
                    <Tooltip title={`Tickets`} placement="bottom">
                      <span className="wm-attr-tab-item">
                        <CustomerServiceOutlined className="wm-attr-tab-icon" />
                      </span>
                    </Tooltip>
                  }
                  key="tickets"
                />
              )}
              {availableTabs.includes("pages") && (
                <TabPane
                  tab={
                    <Tooltip title={`Pages`} placement="bottom">
                      <span className="wm-attr-tab-item">
                        <CopyOutlined className="wm-attr-tab-icon" />
                      </span>
                    </Tooltip>
                  }
                  key="pages"
                />
              )}
              {availableTabs.includes("favourites") && (
                <TabPane
                  tab={
                    <Tooltip title={`Favourites`} placement="bottom">
                      <span className="wm-attr-tab-item">
                        <StarOutlined className="wm-attr-tab-icon" />
                      </span>
                    </Tooltip>
                  }
                  key="favourites"
                />
              )}
            </Tabs>
          </div>
        </div>
      )}

      {/* Content Section - Render AttributeList for all tabs */}
      <div className="wm-attr-tab-content-body">
        {graphInfoLoader ? (
          <div className="wm-attr-loading-skeleton">
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
          </div>
        ) : activeTab ? (
          <AttributeList
            scrollToSelected={scrollToSelected}
            height={height}
            firstPage={firstPage}
            setShowChartSide={setShowChartSide}
            activeDataSource={activeTab}
            onSearchChange={handleAttributeSearch}
          />
        ) : (
          <div className="wm-attr-empty-attributes">
            No attributes available for this field.
          </div>
        )}
      </div>
    </div>
  );
};

export default AttributeTabs;
